import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  BarChart,
  Bar,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  defs,
  linearGradient,
  stop,
} from "recharts";
import PageTitle from "../../layouts/PageTitle";
import { useGetLabourExpenseSummariesQuery } from "../../../store/appApi";
import { selectHasModuleAccess } from "../../../store/authSlice";

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────
const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];
const QUARTER_MAP = {
  Q1: [0, 1, 2],
  Q2: [3, 4, 5],
  Q3: [6, 7, 8],
  Q4: [9, 10, 11],
};
const YEAR_OPTIONS = [2023, 2024, 2025, 2026, 2027];

// ─────────────────────────────────────────────────────────────────────────────
// Mock time-series — company-wide labour expense (₹)
// ─────────────────────────────────────────────────────────────────────────────
const SUMMARY_MOCK = [
  { date: "2024-01-08", amount: 182000 },
  { date: "2024-01-25", amount: 145000 },
  { date: "2024-02-12", amount: 198000 },
  { date: "2024-02-28", amount: 132000 },
  { date: "2024-03-05", amount: 225000 },
  { date: "2024-03-18", amount: 175000 },
  { date: "2024-04-10", amount: 210000 },
  { date: "2024-04-22", amount: 188000 },
  { date: "2024-05-03", amount: 243000 },
  { date: "2024-05-20", amount: 201000 },
  { date: "2024-06-15", amount: 278000 },
  { date: "2024-06-28", amount: 222000 },
  { date: "2024-07-07", amount: 295000 },
  { date: "2024-07-21", amount: 248000 },
  { date: "2024-08-14", amount: 312000 },
  { date: "2024-08-30", amount: 267000 },
  { date: "2024-09-09", amount: 288000 },
  { date: "2024-09-25", amount: 243000 },
  { date: "2024-10-11", amount: 265000 },
  { date: "2024-10-26", amount: 218000 },
  { date: "2024-11-08", amount: 302000 },
  { date: "2024-11-22", amount: 258000 },
  { date: "2024-12-05", amount: 345000 },
  { date: "2024-12-20", amount: 298000 },
  { date: "2025-01-10", amount: 195000 },
  { date: "2025-01-28", amount: 162000 },
  { date: "2025-02-14", amount: 218000 },
  { date: "2025-02-26", amount: 147000 },
  { date: "2025-03-08", amount: 251000 },
  { date: "2025-03-22", amount: 196000 },
  { date: "2025-04-05", amount: 235000 },
  { date: "2025-04-19", amount: 208000 },
  { date: "2025-05-12", amount: 268000 },
  { date: "2025-05-27", amount: 223000 },
  { date: "2025-06-06", amount: 298000 },
  { date: "2025-06-21", amount: 247000 },
  { date: "2025-07-09", amount: 318000 },
  { date: "2025-07-24", amount: 274000 },
  { date: "2025-08-11", amount: 342000 },
  { date: "2025-08-27", amount: 289000 },
  { date: "2025-09-13", amount: 315000 },
  { date: "2025-09-29", amount: 268000 },
  { date: "2025-10-08", amount: 287000 },
  { date: "2025-10-24", amount: 241000 },
  { date: "2025-11-05", amount: 328000 },
  { date: "2025-11-19", amount: 282000 },
  { date: "2025-12-03", amount: 375000 },
  { date: "2025-12-18", amount: 321000 },
  { date: "2026-01-09", amount: 212000 },
  { date: "2026-01-27", amount: 178000 },
  { date: "2026-02-13", amount: 238000 },
  { date: "2026-02-25", amount: 163000 },
  { date: "2026-03-07", amount: 272000 },
  { date: "2026-03-21", amount: 214000 },
  { date: "2026-04-04", amount: 258000 },
  { date: "2026-04-18", amount: 227000 },
  { date: "2026-05-11", amount: 295000 },
  { date: "2026-05-26", amount: 246000 },
  { date: "2026-06-10", amount: 325000 },
  { date: "2026-06-25", amount: 274000 },
];

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────
const fmtINR = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");
const sumArr = (arr) => arr.reduce((a, b) => a + b, 0);
const weekBucket = (day) => (day <= 7 ? 0 : day <= 14 ? 1 : day <= 21 ? 2 : 3);

// ─────────────────────────────────────────────────────────────────────────────
// Shared custom tooltip
// ─────────────────────────────────────────────────────────────────────────────
const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #e9ecef",
        borderRadius: 6,
        padding: "10px 14px",
        fontSize: 12,
      }}
    >
      <p style={{ margin: "0 0 6px", fontWeight: 600, color: "#495057" }}>
        {label}
      </p>
      {payload.map((entry) => (
        <p key={entry.name} style={{ margin: "2px 0", color: entry.color }}>
          {entry.name}: <strong>{fmtINR(entry.value)}</strong>
        </p>
      ))}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Empty-state block (shown when a year has no data)
// ─────────────────────────────────────────────────────────────────────────────
const NoData = ({ year }) => (
  <div className="d-flex flex-column align-items-center justify-content-center py-5 text-muted">
    <svg
      width="52"
      height="52"
      viewBox="0 0 52 52"
      fill="none"
      style={{ opacity: 0.35, marginBottom: 12 }}
    >
      <circle cx="26" cy="26" r="25" stroke="#6c757d" strokeWidth="2" />
      <path
        d="M16 36 L26 16 L36 36"
        stroke="#6c757d"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="26" cy="40" r="2" fill="#6c757d" />
    </svg>
    <p className="mb-0 small">
      No expense data recorded for <strong>{year}</strong>
    </p>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────
const LabourSummary = () => {
  const navigate = useNavigate();
  const hasAccess = useSelector(selectHasModuleAccess("labour", "read"));

  // ── Existing API ───────────────────────────────────────────────────────────
  const { data, isLoading, isFetching, isError } =
    useGetLabourExpenseSummariesQuery(undefined, { skip: !hasAccess });

  const rows = Array.isArray(data?.rows) ? data.rows : [];
  const grandTotal = Number(data?.grandTotal) || 0;
  const loading = isLoading || isFetching;
  const fmt = (n) => Number(n).toLocaleString("en-IN");

  // ── Chart 1 — comparison (YoY / QoQ / MoM) ────────────────────────────────
  const [chartYear, setChartYear] = useState(new Date().getFullYear());
  const [chartQuarter, setChartQuarter] = useState("all");
  const [chartMonth, setChartMonth] = useState("all");
  const [comparisonMode, setComparisonMode] = useState("YoY");

  const { chartData, currentLabel, priorLabel, currentTotal, priorTotal } =
    useMemo(() => {
      const yr = chartYear;
      const prevYr = yr - 1;

      if (comparisonMode === "YoY") {
        const currB = Array(12).fill(0);
        const priorB = Array(12).fill(0);
        SUMMARY_MOCK.forEach(({ date, amount }) => {
          const d = new Date(date);
          const m = d.getMonth();
          if (d.getFullYear() === yr) currB[m] += amount;
          else if (d.getFullYear() === prevYr) priorB[m] += amount;
        });
        return {
          chartData: MONTHS.map((label, i) => ({
            label,
            current: currB[i],
            prior: priorB[i],
          })),
          currentLabel: String(yr),
          priorLabel: String(prevYr),
          currentTotal: sumArr(currB),
          priorTotal: sumArr(priorB),
        };
      }

      if (comparisonMode === "QoQ") {
        const qKey =
          chartQuarter === "all"
            ? `Q${Math.ceil((new Date().getMonth() + 1) / 3)}`
            : chartQuarter;
        const qNum = parseInt(qKey[1], 10);
        const prevQKey = qNum === 1 ? "Q4" : `Q${qNum - 1}`;
        const prevQYear = qNum === 1 ? yr - 1 : yr;
        const currIdxs = QUARTER_MAP[qKey];
        const prevIdxs = QUARTER_MAP[prevQKey];
        const currB = Array(3).fill(0);
        const priorB = Array(3).fill(0);
        SUMMARY_MOCK.forEach(({ date, amount }) => {
          const d = new Date(date);
          const y = d.getFullYear();
          const m = d.getMonth();
          const ci = currIdxs.indexOf(m);
          const pi = prevIdxs.indexOf(m);
          if (y === yr && ci !== -1) currB[ci] += amount;
          if (y === prevQYear && pi !== -1) priorB[pi] += amount;
        });
        return {
          chartData: currIdxs.map((mi, pos) => ({
            label: MONTHS[mi],
            current: currB[pos],
            prior: priorB[pos],
          })),
          currentLabel: `${qKey} ${yr}`,
          priorLabel: `${prevQKey} ${prevQYear}`,
          currentTotal: sumArr(currB),
          priorTotal: sumArr(priorB),
        };
      }

      // MoM
      const mIdx =
        chartMonth === "all"
          ? new Date().getMonth()
          : parseInt(chartMonth, 10) - 1;
      const prevMIdx = mIdx === 0 ? 11 : mIdx - 1;
      const prevMYear = mIdx === 0 ? yr - 1 : yr;
      const currB = Array(4).fill(0);
      const priorB = Array(4).fill(0);
      SUMMARY_MOCK.forEach(({ date, amount }) => {
        const d = new Date(date);
        const y = d.getFullYear();
        const m = d.getMonth();
        const w = weekBucket(d.getDate());
        if (y === yr && m === mIdx) currB[w] += amount;
        if (y === prevMYear && m === prevMIdx) priorB[w] += amount;
      });
      const WEEK_LABELS = ["Week 1", "Week 2", "Week 3", "Week 4"];
      return {
        chartData: WEEK_LABELS.map((label, i) => ({
          label,
          current: currB[i],
          prior: priorB[i],
        })),
        currentLabel: `${MONTHS[mIdx]} ${yr}`,
        priorLabel: `${MONTHS[prevMIdx]} ${prevMYear}`,
        currentTotal: sumArr(currB),
        priorTotal: sumArr(priorB),
      };
    }, [chartYear, chartQuarter, chartMonth, comparisonMode]);

  const rawChange =
    priorTotal > 0
      ? (((currentTotal - priorTotal) / priorTotal) * 100).toFixed(1)
      : null;
  const changeUp = rawChange !== null && Number(rawChange) >= 0;

  // ── Chart 2 — monthly expense trend (area) ─────────────────────────────────
  const [trendYear, setTrendYear] = useState(new Date().getFullYear());

  const { trendData, trendTotal, trendHasData } = useMemo(() => {
    const buckets = Array(12).fill(0);
    SUMMARY_MOCK.forEach(({ date, amount }) => {
      const d = new Date(date);
      if (d.getFullYear() === trendYear) buckets[d.getMonth()] += amount;
    });
    const hasData = buckets.some((v) => v > 0);

    // Find the last month that actually has data so we don't render
    // empty axis ticks for future / unrecorded months.
    const lastFilledIdx = buckets.reduce(
      (last, v, i) => (v > 0 ? i : last),
      -1,
    );
    // For complete years show all 12 months; for partial years stop after
    // the last month with data (+1 empty slot so the area doesn't end abruptly).
    const visibleCount =
      lastFilledIdx === 11 ? 12 : Math.min(lastFilledIdx + 2, 12);
    const slicedMonths = hasData ? MONTHS.slice(0, visibleCount) : MONTHS;

    return {
      trendData: slicedMonths.map((label, i) => ({
        label,
        expense: buckets[i] || null,
      })),
      trendTotal: sumArr(buckets),
      trendHasData: hasData,
    };
  }, [trendYear]);

  // ── Access guard ──────────────────────────────────────────────────────────
  if (!hasAccess) {
    return (
      <>
        <PageTitle activeMenu="Labour Payments" motherMenu="Labour" />
        <div className="row">
          <div className="col-lg-6 mx-auto">
            <div className="card">
              <div className="card-body text-center py-5">
                <i className="fa fa-lock fa-3x text-warning mb-3 d-block" />
                <h5>Access Restricted</h5>
                {/* <p className="text-muted mb-0">
                  Labour payment summaries are only available to{" "}
                  <strong>Super Admin</strong> and <strong>Auditor</strong>{" "}
                  roles.
                </p> */}
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <PageTitle activeMenu="Labour Payments" motherMenu="Labour" />

      <div className="row">
        {/* ══════════════════════════════════════════════════════════════════
              CHART 1 — Comparison (YoY / QoQ / MoM)
          ══════════════════════════════════════════════════════════════════ */}
        <div className="col-lg-6 d-flex flex-column">
          <div className="card mb-4 h-100">
            {/* Filter row */}
            <div
              className="card-header d-flex flex-wrap align-items-center gap-3 py-3"
              style={{
                background: "#fff",
                borderBottom: "1px solid #f0f2f5",
              }}
            >
              <div className="d-flex align-items-center gap-2">
                <label className="mb-0 text-muted small fw-semibold">
                  Year
                </label>
                <select
                  className="form-select form-select-sm"
                  style={{ width: 88 }}
                  value={chartYear}
                  onChange={(e) => setChartYear(Number(e.target.value))}
                >
                  {YEAR_OPTIONS.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>

              <div className="d-flex align-items-center gap-2">
                <label className="mb-0 text-muted small fw-semibold">
                  Quarter
                </label>
                <select
                  className="form-select form-select-sm"
                  style={{ width: 132 }}
                  value={chartQuarter}
                  onChange={(e) => setChartQuarter(e.target.value)}
                  disabled={comparisonMode === "MoM"}
                >
                  <option value="all">All Quarters</option>
                  <option value="Q1">Q1 (Jan–Mar)</option>
                  <option value="Q2">Q2 (Apr–Jun)</option>
                  <option value="Q3">Q3 (Jul–Sep)</option>
                  <option value="Q4">Q4 (Oct–Dec)</option>
                </select>
              </div>

              <div className="d-flex align-items-center gap-2">
                <label className="mb-0 text-muted small fw-semibold">
                  Month
                </label>
                <select
                  className="form-select form-select-sm"
                  style={{ width: 120 }}
                  value={chartMonth}
                  onChange={(e) => setChartMonth(e.target.value)}
                  disabled={comparisonMode !== "MoM"}
                >
                  <option value="all">All Months</option>
                  {MONTHS.map((m, i) => (
                    <option key={m} value={String(i + 1)}>
                      {m}
                    </option>
                  ))}
                </select>
              </div>

              <div className="ms-auto">
                <div className="btn-group" role="group">
                  {["YoY", "QoQ", "MoM"].map((mode) => (
                    <button
                      key={mode}
                      type="button"
                      className={`btn btn-sm ${comparisonMode === mode ? "btn-primary" : "btn-outline-primary"}`}
                      onClick={() => setComparisonMode(mode)}
                    >
                      {mode}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* KPI strip */}
            <div
              className="d-flex flex-wrap gap-0"
              style={{ borderBottom: "1px solid #f0f2f5" }}
            >
              <div
                className="flex-fill px-4 py-3"
                style={{
                  borderRight: "1px solid #f0f2f5",
                  borderLeft: "4px solid #1B3A6B",
                }}
              >
                <span className="d-block text-muted small mb-1">
                  Current · {currentLabel}
                </span>
                <span className="fs-5 fw-bold" style={{ color: "#1B3A6B" }}>
                  {fmtINR(currentTotal)}
                </span>
              </div>
              <div
                className="flex-fill px-4 py-3"
                style={{
                  borderRight: "1px solid #f0f2f5",
                  borderLeft: "4px solid #00C9A7",
                }}
              >
                <span className="d-block text-muted small mb-1">
                  Prior · {priorLabel}
                </span>
                <span className="fs-5 fw-bold" style={{ color: "#00C9A7" }}>
                  {fmtINR(priorTotal)}
                </span>
              </div>
              <div
                className="flex-fill px-4 py-3"
                style={{
                  borderLeft: `4px solid ${rawChange === null ? "#adb5bd" : changeUp ? "#dc3545" : "#198754"}`,
                }}
              >
                <span className="d-block text-muted small mb-1">Change</span>
                <span
                  className="fs-5 fw-bold"
                  style={{
                    color:
                      rawChange === null
                        ? "#adb5bd"
                        : changeUp
                          ? "#dc3545"
                          : "#198754",
                  }}
                >
                  {rawChange === null
                    ? "—"
                    : `${changeUp ? "▲" : "▼"} ${Math.abs(Number(rawChange))}%`}
                </span>
              </div>
            </div>

            {/* Bar chart */}
            <div className="card-body px-3 pt-3 pb-2">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <div>
                  <h6 className="mb-0 fw-semibold">
                    Labour Expense Comparison
                  </h6>
                  <small className="text-muted">
                    {comparisonMode === "YoY" &&
                      "Year-over-Year · Monthly breakdown"}
                    {comparisonMode === "QoQ" &&
                      "Quarter-over-Quarter · Monthly breakdown within quarter"}
                    {comparisonMode === "MoM" &&
                      "Month-over-Month · Weekly breakdown"}
                  </small>
                </div>
                <span
                  className="badge fw-semibold px-3 py-2"
                  style={{
                    background: "#e8f0fe",
                    color: "#1B3A6B",
                    fontSize: 12,
                  }}
                >
                  {comparisonMode}
                </span>
              </div>

              <ResponsiveContainer width="100%" height={260}>
                <BarChart
                  data={chartData}
                  margin={{ top: 8, right: 16, left: 8, bottom: 4 }}
                  barCategoryGap="28%"
                  barGap={4}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    vertical={false}
                    stroke="#f0f2f5"
                  />
                  <XAxis
                    dataKey="label"
                    tick={{ fontSize: 12, fill: "#6c757d" }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    tickFormatter={fmtINR}
                    tick={{ fontSize: 11, fill: "#6c757d" }}
                    axisLine={false}
                    tickLine={false}
                    width={90}
                  />
                  <Tooltip
                    content={<ChartTooltip />}
                    cursor={{ fill: "#f8f9fa" }}
                  />
                  <Legend
                    wrapperStyle={{ fontSize: 13, paddingTop: 12 }}
                    iconType="circle"
                    iconSize={10}
                  />
                  <Bar
                    dataKey="current"
                    name={currentLabel}
                    fill="#1B3A6B"
                    radius={[4, 4, 0, 0]}
                    maxBarSize={48}
                  />
                  <Bar
                    dataKey="prior"
                    name={priorLabel}
                    fill="#00C9A7"
                    radius={[4, 4, 0, 0]}
                    maxBarSize={48}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
        {/* ══════════════════════════════════════════════════════════════════
              CHART 2 — Monthly Expense Trend (area)
          ══════════════════════════════════════════════════════════════════ */}
        <div className="col-lg-6 d-flex flex-column">
          <div className="card mb-4 h-100 d-flex flex-column">
            <div
              className="card-header d-flex flex-wrap align-items-center gap-3 py-3"
              style={{
                background: "#fff",
                borderBottom: "1px solid #f0f2f5",
              }}
            >
              <div>
                <h6 className="mb-0 fw-semibold">Monthly Expense Trend</h6>
                <small className="text-muted">
                  Total labour spend · month by month
                </small>
              </div>

              <div className="d-flex align-items-center gap-2 ms-auto">
                <label className="mb-0 text-muted small fw-semibold">
                  Year
                </label>
                <select
                  className="form-select form-select-sm"
                  style={{ width: 88 }}
                  value={trendYear}
                  onChange={(e) => setTrendYear(Number(e.target.value))}
                >
                  {YEAR_OPTIONS.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>

              {trendHasData && (
                <span
                  className="badge fw-semibold px-3 py-2"
                  style={{
                    background: "#e6faf7",
                    color: "#00897B",
                    fontSize: 12,
                  }}
                >
                  Total {fmtINR(trendTotal)}
                </span>
              )}
            </div>

            <div className="card-body px-3 pt-3 pb-2 flex-grow-1 d-flex flex-column">
              {!trendHasData ? (
                <NoData year={trendYear} />
              ) : (
                <div style={{ flex: 1, minHeight: 0 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart
                      data={trendData}
                      margin={{ top: 8, right: 16, left: 8, bottom: 4 }}
                    >
                      <defs>
                        <linearGradient
                          id="summaryTrendGrad"
                          x1="0"
                          y1="0"
                          x2="0"
                          y2="1"
                        >
                          <stop
                            offset="5%"
                            stopColor="#00C9A7"
                            stopOpacity={0.25}
                          />
                          <stop
                            offset="95%"
                            stopColor="#00C9A7"
                            stopOpacity={0.02}
                          />
                        </linearGradient>
                      </defs>
                      <CartesianGrid
                        strokeDasharray="3 3"
                        vertical={false}
                        stroke="#f0f2f5"
                      />
                      <XAxis
                        dataKey="label"
                        tick={{ fontSize: 12, fill: "#6c757d" }}
                        axisLine={false}
                        tickLine={false}
                      />
                      <YAxis
                        tickFormatter={fmtINR}
                        tick={{ fontSize: 11, fill: "#6c757d" }}
                        axisLine={false}
                        tickLine={false}
                        width={90}
                      />
                      <Tooltip
                        content={<ChartTooltip />}
                        cursor={{
                          stroke: "#00C9A7",
                          strokeWidth: 1,
                          strokeDasharray: "4 4",
                        }}
                      />
                      <Legend
                        wrapperStyle={{ fontSize: 13, paddingTop: 12 }}
                        iconType="circle"
                        iconSize={10}
                      />
                      <Area
                        type="monotone"
                        dataKey="expense"
                        name={`Expense ${trendYear}`}
                        stroke="#00C9A7"
                        strokeWidth={2.5}
                        fill="url(#summaryTrendGrad)"
                        dot={{ r: 4, fill: "#00C9A7", strokeWidth: 0 }}
                        activeDot={{ r: 6, fill: "#00C9A7" }}
                        connectNulls={false}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="row pb-4">
        <div className="col-lg-12">
          {/* ══════════════════════════════════════════════════════════════════
              EXISTING DATA — grand total + summary table (untouched)
          ══════════════════════════════════════════════════════════════════ */}
          <div
            className="card mb-3"
            style={{ background: "#f8f9fa", height: "90px" }}
          >
            <div className="card-body d-flex align-items-center gap-4">
              <div>
                <span className="text-muted small d-block">
                  Total Labourers with Payments
                </span>
                <span className="fw-bold fs-5">
                  {loading ? "…" : rows.length}
                </span>
              </div>
              <div className="ms-auto text-end">
                <span className="text-muted small d-block">
                  Grand Total Paid
                </span>
                <span className="fw-bold fs-5 text-danger">
                  ₹ {loading ? "…" : fmt(grandTotal)}
                </span>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <h5 className="card-title mb-0">Labour-wise Payment Summary</h5>
              <span className="badge badge-primary ms-2">{rows.length}</span>
            </div>
            <div className="card-body">
              {isError && (
                <div className="alert alert-danger">
                  Failed to load labour summaries. Please try again.
                </div>
              )}
              <div className="table-responsive">
                <table className="display dataTable no-footer w-100">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Name</th>
                      <th>Category</th>
                      <th>Type</th>
                      <th>Total Paid (₹)</th>
                      <th>Transactions</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading && (
                      <tr>
                        <td colSpan={7} className="text-center py-4">
                          <span className="spinner-border spinner-border-sm me-2" />
                          Loading…
                        </td>
                      </tr>
                    )}
                    {!loading && rows.length === 0 && !isError && (
                      <tr>
                        <td colSpan={7} className="text-center py-4 text-muted">
                          No labour payments recorded yet.
                        </td>
                      </tr>
                    )}
                    {!loading &&
                      rows.map((row, idx) => (
                        <tr key={row._id || idx}>
                          <td className="text-muted small">{idx + 1}</td>
                          <td className="fw-semibold">{row.name || "—"}</td>
                          <td className="text-muted small">
                            {row.laborCat || "—"}
                          </td>
                          <td
                            className="text-muted small"
                            style={{ textTransform: "capitalize" }}
                          >
                            {row.typeOfLabour || "—"}
                          </td>
                          <td className="fw-semibold text-danger">
                            ₹ {fmt(row.totalPayment)}
                          </td>
                          <td className="text-muted small">{row.txnCount}</td>
                          <td>
                            <button
                              type="button"
                              className="btn btn-xs btn-outline-primary"
                              onClick={() =>
                                navigate(
                                  `/payments/finance/labour-detail/${row._id}`,
                                )
                              }
                            >
                              <i className="fa fa-list me-1" />
                              Ledger
                            </button>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default LabourSummary;
